/*
  Restores the global, locale-aware <Head> tags that were emitted by `pages/_app.tsx`
  before the SA-6876 metadata refactor and are NOT produced by the per-page
  `components/MetaData` component:
    - viewport meta
    - og:site_name, og:url, og:locale:alternate (only when `og` is present)
    - GEO hreflang links: rel="alternate" x-default + one per `localeOrder` entry

  URL-building logic is copied verbatim from the original `pages/_app.tsx` render.
*/

import React, { FC } from 'react'
import Head from 'next/head'
import { NextRouter } from 'next/router'
import { createNewURL } from 'lib/storeLocale/client'
import { getGeoLocationMetaData } from 'selectors/user/cookie' // TODO - Move to Locale
import { getPagePath } from 'selectors/page'
import { urlQuery } from 'lib/helpers'
import parse from 'lib/url/parse'
import { getSafeHttpUrl } from 'lib/sanitize'
import { PageInitialProps } from 'types/metadata'
import { User } from 'types/User'
import { Shared } from 'types/Shared'
import { Page } from 'types/Page'

const DEFAULT = `en-us`

/* We need a way to have clean path for homepage ( Product ASK )
   Ex: `.com/` => we want `.com`
   This helper will only clean up specific rule
*/
const cleanHomePage = (url: string): string => {
  return url.match(/com[/]$/) ? url.replace(/\/$/, '') : url
}

type InitialState = {
  user: User
  shared: Shared
  page: Page
}

type AppGeoMetadataProps = {
  initialProps?: PageInitialProps
  initialState: InitialState
  router?: NextRouter
}

const AppGeoMetadata: FC<AppGeoMetadataProps> = ({ initialState, router }) => {
  const geoLocationMetaData = getGeoLocationMetaData(initialState)
  const urlPath = router?.asPath || getPagePath(initialState)
  const { storeLocale: currentStoreLocale, localeOrder } = geoLocationMetaData

  // Create a storeLocale valid url w/ only PAGE as a valid query param for the canonical
  const result = parse(urlPath)
  const { origin, pathname: path, query } = result
  let queryParams = ''

  let cleanPath = path
  /* Remove trailing SLASH for SEO
    Ex: `.com/stories/` => `.com/stories`
    We don't want duplicate content
  */
  if (cleanPath.match(/.*[/]$/)) {
    cleanPath = cleanPath.replace(/\/$/, '')
  }

  if (query.page) {
    queryParams = urlQuery({
      page: query.page,
    })
  }

  const serverCanonicalUrl = `${origin}${cleanPath}${queryParams}` // The server does NOT send back `storeLocale` - we will need to add manually
  // Guard the URL-derived (tainted) value: only emit well-formed http(s) URLs
  // into the link `href`, rejecting javascript:/data: and malformed schemes.
  const canonicalUrl = getSafeHttpUrl(
    cleanHomePage(createNewURL(serverCanonicalUrl, currentStoreLocale))
  )
  const xDefaultUrl = getSafeHttpUrl(cleanHomePage(createNewURL(serverCanonicalUrl, `en-us`)))

  return (
    <Head>
      <meta
        key='viewport'
        name='viewport'
        content='width=device-width, initial-scale=1.0, maximum-scale=5.0'
      />

      {/* Canonical — always the store-locale-aware URL, exactly like the old
          _app.tsx (createNewURL with the current storeLocale, e.g. `/en-gb`).
          This is the single source of <link rel="canonical">; MetaData no longer
          renders one. WordPress's own canonical is stripped server-side in
          wpCms/helpers `cleanTags`, so this is the only one there too. */}
      <link key='canonical' rel='canonical' href={canonicalUrl} />

      {/* GEO Links */}
      <link key='xdefault' rel='alternate' href={xDefaultUrl} hrefLang='x-default' />
      {localeOrder.map((locale, index) => {
        // Do NOT add a store locale for US
        const isDefault = DEFAULT === locale
        const storeLocale = isDefault ? '' : `${locale}`
        const newURL = getSafeHttpUrl(cleanHomePage(createNewURL(xDefaultUrl, storeLocale)))
        return (
          <link
            key={`link-store-locale-${index}`}
            rel='alternate'
            href={newURL}
            hrefLang={locale}
          />
        )
      })}
    </Head>
  )
}

export default AppGeoMetadata
